import { useId, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { listImportProposals } from "../../api/content/importProposalsApi";
import type { ChangeSetState } from "../../api/content/reviewDecoders";
import { appPaths } from "../../shared/routing/appPaths";
import { formatTimeAgo } from "../../shared/formatting/relativeTimes";
import { useLoad } from "../../shared/loading/useLoad";
import ReviewStateBadge from "../reviews/list/ReviewStateBadge";
import LoadStatus from "../../ui/feedback/LoadStatus";

/** The project's corpus imports in one state, on Documents, where import lives (Q-30, F108). Nothing when none. */
export default function ImportProposalList({ projectId, state }: { projectId: string; state: ChangeSetState | null }) {
  const { t, i18n } = useTranslation();
  const headingId = useId();
  const load = useCallback((signal: AbortSignal) => listImportProposals(projectId, state, signal), [projectId, state]);
  const { state: loaded, reload } = useLoad(load);
  if (loaded.status !== "loaded") return <LoadStatus state={loaded} loadingLabel={t("common.loading")} onRetry={reload} compact />;
  if (loaded.value.length === 0) {
    return null;
  }
  return (
    <section className="space-y-2" aria-labelledby={headingId}>
      <h3 id={headingId} className="app-title text-base font-semibold">{t("importProposals.list.title")}</h3>
      <ul className="space-y-2">
        {loaded.value.map((changeSet) => (
          <li key={changeSet.changeSetId}>
            <Link to={appPaths.importProposal(changeSet.changeSetId)} className="app-card block space-y-1 p-3 hover:shadow">
              <span className="flex items-center gap-2">
                <span className="app-title flex-1 text-sm font-semibold">{changeSet.title}</span>
                <ReviewStateBadge state={changeSet.state} />
              </span>
              <span className="app-text-muted block text-xs">{formatTimeAgo(changeSet.openedAt, i18n.resolvedLanguage)}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
